/**
 * Moving through a list too long to show at once.
 *
 * One hand-rolled instance existed, as two bare buttons with no sense of
 * position. This says where you are, which is what makes the control usable
 * rather than merely operable.
 *
 * @module components/ui/Pagination
 */

import React from "react";

import { Button } from "./Button";
import { EditLockExempt } from "./EditLock";

export interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Names what is being paged, e.g. "Audit events". */
  "aria-label": string;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <Pagination aria-label="Audit events" page={page} pageCount={pages} onPageChange={setPage} />
 * ```
 */
export function Pagination({
  page,
  pageCount,
  onPageChange,
  "aria-label": label,
  className = "",
  "data-testid": testId,
}: PaginationProps): React.ReactElement | null {
  if (pageCount <= 1) {
    return null;
  }
  return (
    <EditLockExempt>
      <nav
        aria-label={label}
        className={`flex items-center justify-center gap-3 ${className}`.trim()}
        data-testid={testId}
      >
        <Button
          size="sm"
          icon="caret-left"
          // Dimmed, not disabled: the browser blurs a control as it becomes
          // disabled, dropping focus to the body on reaching page one. daisyUI
          // already paints aria-disabled at 20% ink; an opacity utility on top
          // would halve the focus ring too.
          aria-disabled={page <= 1 || undefined}
          onClick={
            page <= 1
              ? undefined
              : () => {
                  onPageChange(page - 1);
                }
          }
          data-testid="pagination-previous"
        >
          Previous
        </Button>
        {/* Announced on change, so someone who cannot see the control move still
          learns where they landed. */}
        <span aria-live="polite" className="text-md text-muted">
          Page {page} of {pageCount}
        </span>
        <Button
          size="sm"
          iconEnd="caret-right"
          aria-disabled={page >= pageCount || undefined}
          onClick={
            page >= pageCount
              ? undefined
              : () => {
                  onPageChange(page + 1);
                }
          }
          data-testid="pagination-next"
        >
          Next
        </Button>
      </nav>
    </EditLockExempt>
  );
}
